import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";

const API_BASE = "https://eluvent.com/api";

const ExamPredictor = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTest, setExpandedTest] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setError("Please go to the chat first to generate your session.");
      setLoading(false);
      return;
    }
    axios
      .post(`${API_BASE}/exam-predictor`, { session_id: sessionId })
      .then((res) => {
        setData(res.data);
        setError(null);
      })
      .catch(() => {
        setError(null);
        setData({
          predicted_score: null,
          message: "Take 5 topic tests (20 questions each) to unlock your board exam prediction! 📝",
          tests_completed: 0,
          tests_needed: 5,
          tests: []
        });
      })
      .finally(() => setLoading(false));
  }, [sessionId]);

  if (!sessionId) {
    return (
      <div style={{ maxWidth: 600, margin: "100px auto", textAlign: "center", fontFamily: "Nunito, sans-serif" }}>
        <h2>🔮 No session ID</h2>
        <p>Please access this from the main chat page.</p>
        <Link to="/" style={{ color: "#fca311", fontWeight: 700 }}>← Back to Chat</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 40, fontFamily: "Nunito, sans-serif", background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)", minHeight: "100vh" }}>
        <div style={{ fontSize: 48, marginBottom: 8, animation: "bounce 1s infinite" }}>🤖</div>
        <p style={{ fontSize: 18, fontWeight: 600, color: "#1e293b" }}>🔮 Analyzing your test data...</p>
        <div style={{ width: 40, height: 40, border: "4px solid #e2e8f0", borderTop: "4px solid #7c3aed", borderRadius: "50%", animation: "spin 1s linear infinite", margin: "20px auto" }}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: "center", padding: 40, color: "red", fontFamily: "Nunito, sans-serif" }}>
        <p>{error}</p>
        <Link to="/" style={{ color: "#fca311", fontWeight: 700 }}>← Back to Chat</Link>
      </div>
    );
  }

  if (!data) return null;

  // ---------- PROGRESS SCREEN (less than 5 tests) ----------
  if (data.predicted_score === null || data.predicted_score === undefined) {
    return (
      <div style={{ maxWidth: 800, margin: "30px auto", padding: 20, fontFamily: "Nunito, sans-serif" }}>
        <h1 style={{ textAlign: "center", color: "#1e293b" }}>🔮 Exam Predictor</h1>
        <div style={{ background: "#f0f9ff", borderRadius: 12, padding: 20, margin: "20px 0", borderLeft: "4px solid #0ea5e9" }}>
          <p style={{ fontSize: 16, color: "#0369a1" }}>{data.message}</p>
          <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ flex: 1, height: 10, background: "#e2e8f0", borderRadius: 5, overflow: "hidden" }}>
              <div style={{ width: `${((data.tests_completed || 0) / (data.tests_needed || 5)) * 100}%`, height: "100%", background: "#10b981", borderRadius: 5 }} />
            </div>
            <span style={{ fontSize: 13, color: "#475569" }}>{data.tests_completed || 0}/{data.tests_needed || 5} tests</span>
          </div>
        </div>
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <Link to={`/test?session_id=${sessionId}`} style={{ padding: "12px 24px", background: "#fca311", color: "white", borderRadius: 30, textDecoration: "none", fontWeight: 700 }}>
            📝 Take a Test (20 Questions)
          </Link>
          <br />
          <Link to="/" style={{ display: "inline-block", marginTop: 10, color: "#64748b" }}>← Back to Chat</Link>
        </div>
      </div>
    );
  }

  // ---------- FULL PREDICTION (5+ tests) ----------
  return (
    <div style={{ maxWidth: 800, margin: "30px auto", padding: 20, fontFamily: "Nunito, sans-serif" }}>
      <h1 style={{ textAlign: "center", color: "#1e293b" }}>🔮 Your Board Exam Prediction</h1>
      <div style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white", borderRadius: 20, padding: 24, textAlign: "center", margin: "20px 0", boxShadow: "0 8px 20px rgba(102,126,234,0.3)", animation: "pulseScore 2s infinite" }}>
        <p style={{ fontSize: 16, margin: 0, opacity: 0.9 }}>Your estimated score</p>
        <p style={{ fontSize: 48, fontWeight: 800, margin: "8px 0" }}>{data.predicted_score}%</p>
        <p style={{ fontSize: 18, margin: 0, opacity: 0.9 }}>Range: {data.predicted_range || `${data.predicted_score}% - ${data.predicted_score}%`}</p>
        <p style={{ fontSize: 14, opacity: 0.9, marginTop: 8 }}>Based on 5 tests (100 questions) with weighted difficulty</p>
      </div>
      <div style={{ textAlign: "center", marginTop: 20 }}>
        <Link to={`/test?session_id=${sessionId}`} style={{ padding: "12px 24px", background: "#fca311", color: "white", borderRadius: 30, textDecoration: "none", fontWeight: 700, marginRight: 10 }}>
          📝 Take Another Test
        </Link>
        <Link to="/" style={{ padding: "12px 24px", background: "white", color: "#64748b", border: "1px solid #e2e8f0", borderRadius: 30, textDecoration: "none", fontWeight: 600 }}>
          ← Back to Chat
        </Link>
      </div>
      <p style={{ textAlign: "center", fontSize: 12, color: "#94a3b8", marginTop: 20 }}>
        ⚠️ This is an estimate based on your practice test results. Actual board exam scores may vary. Keep practicing to improve!
      </p>
      <style>{`
        @keyframes bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes pulseScore { 0% { box-shadow: 0 0 0 0 rgba(102,126,234,0.4); } 70% { box-shadow: 0 0 0 15px rgba(102,126,234,0); } 100% { box-shadow: 0 0 0 0 rgba(102,126,234,0); } }
      `}</style>
    </div>
  );
};

export default ExamPredictor;